import { useState } from 'react';
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';

import { cx } from './cx.js';
import { IconAlert, IconEye, IconEyeOff } from './icons.js';

/**
 * Primitivas visuais do design system.
 *
 * Ficam juntas de proposito enquanto sao poucas: cada uma tem 10-20 linhas e
 * ler o arquivo inteiro mostra a linguagem visual completa. Quando alguma
 * ganhar variantes de verdade (menu, tabela, modal), ela sai daqui para o seu
 * proprio arquivo.
 */

// Reexportados para que as telas continuem importando tudo de `ui.js` — o
// arquivo e a porta unica do design system, mesmo quando a implementacao mora
// ao lado.
export { cx } from './cx.js';
export { Select, type SelectOption } from './Select.js';
export { Pagination, useCursorPages, resetting, type CursorPages } from './Pagination.js';

// ---------------------------------------------------------------------------
// Superficies
// ---------------------------------------------------------------------------

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={cx('rounded-card border border-line bg-surface', className)}>{children}</div>
  );
}

export function CardHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4 p-5 sm:p-6">
      <div>
        <h2 className="text-xl font-bold tracking-tight sm:text-2xl">{title}</h2>
        {subtitle && <p className="mt-1 text-sm text-muted">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

/** Card de numero do topo das telas: rotulo, valor grande, nota embaixo. */
export function StatCard({
  label,
  value,
  hint,
  highlight = false,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  highlight?: boolean;
}) {
  return (
    <Card className="p-4 sm:p-5">
      <p className="text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-muted">{label}</p>
      <p
        className={cx(
          // No celular quatro indicadores empilhados em text-4xl eram uma dobra
          // inteira de numeros antes da lista. Em duas colunas, 3xl e o que cabe.
          'mt-2 text-3xl font-extrabold tracking-tight tabular-nums sm:text-4xl',
          highlight ? 'text-accent' : 'text-fg',
        )}
      >
        {value}
      </p>
      {hint && <p className="mt-1 text-sm text-faint">{hint}</p>}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Cabecalho de pagina
// ---------------------------------------------------------------------------

/**
 * Titulo da tela — e so isso.
 *
 * Ja foi um bloco de tres andares (rotulo colorido, titulo em 3.5rem com ponto
 * final, e uma linha de subtitulo) copiado da landing page. Dentro do sistema
 * aquilo nao informava nada: quem esta logado ja escolheu a tela no menu, ja
 * sabe em que secao esta, e ja comprou o produto. O que sobrava era meia dobra
 * de texto empurrando os numeros e a lista — o conteudo real — para baixo.
 *
 * Por isso `subtitle` e opcional e existe so para instrucao que o conteudo da
 * tela nao da sozinho; e `action` traz o botao principal para a linha do
 * titulo, em vez de gastar um card inteiro para anunciar.
 *
 * Sem margem propria: o ritmo vertical entre os blocos da pagina e do <main>
 * da AppShell (space-y-6). Header que carrega o proprio mb-8 briga com esse
 * ritmo e cada pagina acaba compensando com um mt-* diferente.
 */
export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 pt-1">
      <div className="min-w-0">
        <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">{title}</h1>
        {subtitle && <p className="mt-1 max-w-2xl text-sm text-muted">{subtitle}</p>}
      </div>
      {action}
    </header>
  );
}

// ---------------------------------------------------------------------------
// Badge de status
// ---------------------------------------------------------------------------

export function StatusBadge({
  tone,
  children,
}: {
  tone: 'accent' | 'neutral' | 'warn';
  children: ReactNode;
}) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-2 rounded-pill border px-3 py-1.5 text-[0.7rem] font-bold uppercase tracking-wider',
        tone === 'accent'
          ? 'border-accent/40 bg-accent/10 text-accent'
          : tone === 'warn'
            ? 'border-warn/40 bg-warn/10 text-warn'
            : 'border-line bg-surface-2 text-muted',
      )}
    >
      <span
        aria-hidden
        className={cx(
          'size-1.5 rounded-full',
          tone === 'accent' ? 'bg-accent' : tone === 'warn' ? 'bg-warn' : 'bg-faint',
        )}
      />
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Acoes
// ---------------------------------------------------------------------------

/** Circulo girando — nasce aqui porque so o Button e o leitor precisam dele. */
export function Spinner({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={cx('size-4 animate-spin', className)}>
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2.5" opacity="0.25" />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'ghost' | 'quiet';
  /** Mostra o spinner e bloqueia o clique — sem precisar mexer no rotulo. */
  loading?: boolean;
};

export function Button({
  variant = 'primary',
  loading = false,
  className,
  children,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <button
      {...props}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        // `min-h-11` = 44px, o minimo de alvo de toque recomendado. Sem ele os
        // botoes de acao das listas (`py-1.5`) davam ~32px de altura — no celular
        // da portaria, o botao que precisa de duas tentativas.
        'inline-flex min-h-11 items-center justify-center gap-2 rounded-pill px-5 py-3 text-sm font-bold transition',
        'disabled:cursor-not-allowed disabled:opacity-50',
        variant === 'primary' && 'bg-accent text-on-accent hover:brightness-95 active:brightness-90',
        variant === 'ghost' && 'border border-line bg-surface-2 text-fg hover:border-faint',
        variant === 'quiet' && 'text-muted hover:bg-surface-2 hover:text-fg',
        className,
      )}
    >
      {loading && <Spinner />}
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Formulario
// ---------------------------------------------------------------------------

export function Field({
  label,
  error,
  className,
  children,
}: {
  /** ReactNode, e nao string: alguns campos carregam um selo ao lado do nome. */
  label: ReactNode;
  error?: string;
  /** Para o campo dizer quanto ocupa quando vive dentro de uma grid. */
  className?: string;
  children: ReactNode;
}) {
  return (
    <label className={cx('block', className)}>
      {/* cursor no rotulo, nao no <label> inteiro: clicar no texto foca o
          campo, mas dentro do proprio input o cursor tem que continuar de
          texto. */}
      <span className="cursor-pointer text-sm font-semibold text-muted">{label}</span>
      <div className="mt-1.5">{children}</div>
      {error && <span className="mt-1 block text-sm text-danger">{error}</span>}
    </label>
  );
}

/** Classes compartilhadas por input, select e textarea — um lugar so. */
export const CONTROL_CLASS =
  'w-full rounded-2xl border border-line bg-surface-2 px-4 py-3 text-fg placeholder:text-faint focus:border-accent/60 focus:outline-none disabled:opacity-60';

export function TextInput({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={cx(CONTROL_CLASS, className)} />;
}

/**
 * Campo com mascara de digitacao.
 *
 * O valor que sai no `onChange` e o valor MASCARADO — quem usa converte para
 * digitos na hora de enviar (`phoneToE164`). O contrario, guardar digitos no
 * estado e mascarar so na renderizacao, faz o cursor pular para o fim a cada
 * tecla, e num tablet de portaria isso e a diferenca entre corrigir um digito e
 * redigitar o telefone inteiro.
 *
 * `inputMode="numeric"` abre o teclado numerico no celular; `type` continua
 * `text` porque `type="number"` recusaria os parenteses e o hifen da mascara.
 */
export function MaskedInput({
  mask,
  value,
  onValueChange,
  className,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> & {
  mask: (value: string) => string;
  value: string;
  onValueChange: (value: string) => void;
}) {
  return (
    <input
      {...props}
      type="text"
      inputMode="numeric"
      value={mask(value)}
      onChange={(event) => onValueChange(mask(event.target.value))}
      className={cx(CONTROL_CLASS, className)}
    />
  );
}

/**
 * Senha com botao de revelar.
 *
 * Portaria digita a senha em tablet compartilhado e erra muito; sem o olho, o
 * unico caminho para conferir e apagar tudo e comecar de novo.
 */
export function PasswordInput({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="relative">
      <input
        {...props}
        type={visible ? 'text' : 'password'}
        className={cx(CONTROL_CLASS, 'pr-12', className)}
      />
      <button
        type="button"
        onClick={() => setVisible((value) => !value)}
        aria-label={visible ? 'Ocultar senha' : 'Mostrar senha'}
        title={visible ? 'Ocultar senha' : 'Mostrar senha'}
        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-xl p-2 text-faint transition hover:bg-surface hover:text-fg"
      >
        {visible ? <IconEyeOff className="size-5" /> : <IconEye className="size-5" />}
      </button>
    </div>
  );
}

/** Mensagem de erro/aviso com icone — o texto solto passava despercebido. */
export function Alert({ tone = 'danger', children }: { tone?: 'danger' | 'warn'; children: ReactNode }) {
  return (
    <div
      role="alert"
      className={cx(
        'flex items-start gap-2.5 rounded-2xl border px-4 py-3 text-sm',
        tone === 'danger' ? 'border-danger/30 bg-danger/10 text-danger' : 'border-warn/30 bg-warn/10 text-warn',
      )}
    >
      <IconAlert className="mt-0.5 size-4 shrink-0" />
      <span className="text-fg">{children}</span>
    </div>
  );
}

export function SearchInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div className="relative">
      <span aria-hidden className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-faint">
        ⌕
      </span>
      <TextInput {...props} type="search" className="pl-10" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Estados
// ---------------------------------------------------------------------------

export function EmptyState({ title, description }: { title: string; description: string }) {
  return (
    <div className="px-6 py-14 text-center">
      <p className="font-semibold text-fg">{title}</p>
      <p className="mt-1 text-sm text-muted">{description}</p>
    </div>
  );
}

export function LoadingRows({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-3 p-5">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="h-16 animate-pulse rounded-2xl bg-surface-2" />
      ))}
    </div>
  );
}

/**
 * Assinatura da casa. Fica num componente so para que o nome do grupo apareca
 * identico em toda tela — marca escrita a mao em cada lugar diverge no dia em
 * que uma delas muda.
 */
export function MadeByAzkoGroup({ className }: { className?: string }) {
  return (
    <p className={cx('text-xs text-faint', className)}>
      made by <span className="font-semibold text-muted">Azko Group</span>
    </p>
  );
}

/**
 * Mostrado quando a API responde 402: a acao existe, mas nao no plano atual.
 * Erro de plano e oportunidade de venda — nunca deve virar "algo deu errado".
 */
export function PlanBlock({ message }: { message: string }) {
  return (
    <Card className="border-accent/30 bg-accent/5 p-5">
      <p className="eyebrow">Recurso do plano</p>
      <p className="mt-2 font-semibold">{message}</p>
      <Button className="mt-4" onClick={() => window.location.assign('/plano')}>
        Ver planos
      </Button>
    </Card>
  );
}
